import { Quad } from "./contour";
import { GuidanceMessage } from "./analyzeFrame";

export interface GeometryResult {
  aspectRatioScore: number;
  coverageScore: number;
  tiltScore: number;
  perspectiveScore: number;
  cornersVisibleScore: number;
  mrzVisibleScore: number;
  geometryScore: number;
  isValid: boolean;
  invalidReason?: string;
}

// Passport TD3: 125×88 mm → width/height = 1.42 (landscape).
// ID card TD1: 85.6×54 mm → width/height = 1.585 (landscape).
const ASPECT = { TD3: 125 / 88, TD1: 85.6 / 54 } as const;

/**
 * Validates detected document geometry against known passport/card proportions.
 *
 * Checks: aspect ratio, frame coverage, tilt, perspective distortion,
 * corner visibility (clipping), and MRZ zone visibility.
 */
export function validateGeometry(
  quad: Quad,
  canvasW: number,
  canvasH: number,
  documentType: "TD1" | "TD3",
  mrzStartRow: number,
): GeometryResult {
  const [tl, tr, br, bl] = quad.corners;

  const topW = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]);
  const botW = Math.hypot(br[0] - bl[0], br[1] - bl[1]);
  const leftH = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]);
  const rightH = Math.hypot(br[0] - tr[0], br[1] - tr[1]);

  const avgW = (topW + botW) / 2;
  const avgH = (leftH + rightH) / 2;
  const detectedAspect = avgW / Math.max(avgH, 1);

  // ── Aspect ratio ──────────────────────────────────────────────────────────
  const expectedAspect = ASPECT[documentType];
  const aspectDiff = Math.abs(detectedAspect - expectedAspect) / expectedAspect;
  const aspectRatioScore = Math.max(0, 1 - aspectDiff / 0.3); // 30% tolerance

  // ── Coverage ─────────────────────────────────────────────────────────────
  // Expect the document to fill at least 35% of the guide canvas.
  const coverageScore = Math.min(1, quad.area / (canvasW * canvasH * 0.35));

  // ── Tilt — angle of the top edge from horizontal ──────────────────────────
  const topAngleDeg =
    Math.abs(Math.atan2(tr[1] - tl[1], tr[0] - tl[0]) * (180 / Math.PI));
  const tiltScore = Math.max(0, 1 - topAngleDeg / 20); // 20° max

  // ── Perspective distortion — opposite sides should be similar in length ───
  const widthRatio =
    Math.min(topW, botW) / Math.max(topW, botW, 1);
  const heightRatio =
    Math.min(leftH, rightH) / Math.max(leftH, rightH, 1);
  const perspectiveScore = (widthRatio + heightRatio) / 2;

  // ── Corner visibility — all four must be within the canvas ────────────────
  const margin = 3;
  const cornersVisible = quad.corners.every(
    ([x, y]) =>
      x >= margin && x <= canvasW - margin && y >= margin && y <= canvasH - margin,
  );
  const cornersVisibleScore = cornersVisible ? 1 : 0;

  // ── MRZ zone — bottom edge of quad should reach the MRZ band ─────────────
  const bottomY = Math.max(br[1], bl[1]);
  const mrzVisibleScore =
    bottomY >= mrzStartRow
      ? 1
      : Math.max(0, 1 - (mrzStartRow - bottomY) / (canvasH * 0.12));

  // ── Hard blocks ───────────────────────────────────────────────────────────
  if (!cornersVisible) {
    return {
      aspectRatioScore,
      coverageScore,
      tiltScore,
      perspectiveScore,
      cornersVisibleScore: 0,
      mrzVisibleScore,
      geometryScore: 0,
      isValid: false,
      invalidReason: "corners outside frame",
    };
  }
  if (coverageScore < 0.2) {
    return {
      aspectRatioScore,
      coverageScore,
      tiltScore,
      perspectiveScore,
      cornersVisibleScore,
      mrzVisibleScore,
      geometryScore: 0,
      isValid: false,
      invalidReason: "document too small in frame",
    };
  }

  // ── Weighted geometry score ───────────────────────────────────────────────
  const geometryScore =
    aspectRatioScore * 0.25 +
    coverageScore * 0.25 +
    tiltScore * 0.15 +
    perspectiveScore * 0.20 +
    cornersVisibleScore * 0.10 +
    mrzVisibleScore * 0.05;

  return {
    aspectRatioScore,
    coverageScore,
    tiltScore,
    perspectiveScore,
    cornersVisibleScore,
    mrzVisibleScore,
    geometryScore,
    isValid: geometryScore >= 0.50,
    invalidReason: geometryScore < 0.50 ? "geometry below threshold" : undefined,
  };
}

/** Maps geometry validation failures to a user-visible guidance message. */
export function geometryGuidance(result: GeometryResult): GuidanceMessage {
  if (result.cornersVisibleScore < 1) return "Show all four corners";
  if (result.coverageScore < 0.4) return "Move closer";
  if (result.tiltScore < 0.5) return "Straighten the document";
  if (result.mrzVisibleScore < 0.5) return "Align MRZ inside the frame";
  return "Center the passport";
}
